import { Undo2, Zap } from 'lucide-react';
import { BustEvent, GameState } from '../types/darts';
import { adviseCheckout } from '../utils/checkout';
import { useAppTheme } from '../hooks/useAppTheme';
import { useT } from '../hooks/useT';

interface ScoreboardX01Props {
  gameState: GameState;
  onSwitchInputMethod: () => void;
  canSwitchInput: boolean;
  onUndo: () => void;
  canUndo: boolean;
  bustEvent?: BustEvent | null;
}

const pill =
  'flex min-h-11 items-center gap-1.5 rounded-2xl px-3 text-sm font-semibold whitespace-nowrap transition-all duration-150 active:scale-95';

export function ScoreboardX01({ gameState, onSwitchInputMethod, canSwitchInput, onUndo, canUndo, bustEvent = null }: ScoreboardX01Props) {
  const { t, tf } = useT();
  const { theme, isDark } = useAppTheme();
  const { config, players } = gameState;
  const isCountUp = config.gameMode === 'countup';
  const activePlayer = players[gameState.activePlayerIndex];
  const activeScore = gameState.scores[activePlayer.id];
  // Het advies loopt mee met de lopende beurt: rest na de gegooide darts, met de darts die nog over zijn.
  const pending = gameState.currentTurnDarts;
  const pendingSum = pending.reduce((acc, d) => acc + d.score, 0);
  const liveRemaining = activeScore - pendingSum;
  const advice = !isCountUp ? adviseCheckout(liveRemaining, config.outMode, 3 - pending.length) : null;
  const isTurnTotal = config.inputMethod === 'turn-total';
  const isMulti = players.length >= 3;

  const outModeLabel = config.outMode === 'single' ? t('outSingle') : config.outMode === 'master' ? t('outMaster') : t('outDouble');
  const routeLabel = config.outMode === 'single' ? t('routeSingle') : config.outMode === 'master' ? t('routeMaster') : t('routeDouble');
  const otherMethod = isTurnTotal ? t('inputShortDart') : t('inputShortTotal');

  return (
    <div className="mx-auto w-full max-w-xl space-y-2">
      <div className="flex items-center justify-between gap-2 rounded-2xl bg-black/5 px-3 py-1 dark:bg-white/5">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${theme.badgeBg} ${theme.badgeText}`}>
            {isCountUp ? t('modeCountUp') : config.gameMode}
          </span>
          <span className="truncate text-xs font-medium text-slate-600 dark:text-slate-400">
            {isCountUp
              ? tf('countUpTarget', { n: config.startingScore })
              : `${tf('firstToShort', { n: config.firstToLegs })} · ${outModeLabel}`}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={onSwitchInputMethod}
            disabled={!canSwitchInput}
            aria-label={canSwitchInput ? tf('switchInput', { method: otherMethod }) : t('switchInputDisabled')}
            title={canSwitchInput ? undefined : t('switchInputDisabled')}
            className={`${pill} bg-white text-slate-800 disabled:opacity-40 dark:bg-slate-800/80 dark:text-slate-100`}
          >
            <Zap size={16} className={theme.textAccent} aria-hidden />
            {isTurnTotal ? t('inputShortTotal') : t('inputShortDart')}
          </button>
          {canUndo && (
            <button onClick={onUndo} aria-label={t('undoLabel')} className={`${pill} bg-red-500/15 text-red-800 dark:text-red-300`}>
              <Undo2 size={16} aria-hidden />
              <span className="hidden sm:inline">{t('undoShort')}</span>
            </button>
          )}
        </div>
      </div>

      <div className={`grid gap-2 ${players.length === 1 ? 'grid-cols-1' : players.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {players.map((player, idx) => {
          const isActive = idx === gameState.activePlayerIndex;
          const isBusted = bustEvent?.playerId === player.id;
          // Aan de beurt: de score loopt mee met de darts die deze beurt al gegooid zijn.
          const score = isActive ? (isCountUp ? gameState.scores[player.id] + pendingSum : liveRemaining) : gameState.scores[player.id];
          const legs = gameState.legsWon[player.id] || 0;
          const dartsThrown = gameState.dartsThrown[player.id] || 0;
          const totalPoints = gameState.totalPointsScored[player.id] || 0;
          const avg3Dart = dartsThrown > 0 ? ((totalPoints / dartsThrown) * 3).toFixed(1) : '0.0';
          const pct = isCountUp && config.startingScore > 0 ? Math.min(100, Math.round((score / config.startingScore) * 100)) : 0;
          const playerAdvice = isCountUp ? null : isActive ? advice : adviseCheckout(score, config.outMode, 3);
          const first9Darts = gameState.first9Darts[player.id] || 0;
          const first9Avg = first9Darts > 0 ? ((gameState.first9Points[player.id] / first9Darts) * 3).toFixed(1) : '–';
          const attempts = gameState.checkoutAttempts[player.id] || 0;
          const hits = gameState.checkoutHits[player.id] || 0;

          return (
            <section
              key={player.id}
              aria-current={isActive ? 'true' : undefined}
              aria-label={player.name}
              className={`relative flex flex-col justify-between overflow-hidden rounded-3xl transition-all duration-300 ${isMulti ? 'p-2.5 sm:p-3' : 'p-3 sm:p-4'} ${
                isBusted
                  ? 'animate-bust-shake bg-red-500/20 text-red-950 ring-2 ring-red-600 dark:text-red-100'
                  : isActive
                  ? isDark
                    ? 'bg-emerald-950/60 text-emerald-50 ring-2 ring-emerald-500/60'
                    : 'bg-emerald-50 text-emerald-950 ring-2 ring-emerald-600/50'
                  : isDark
                  ? 'bg-white/5 text-slate-200'
                  : 'bg-white text-slate-800 shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between gap-1">
                <h2 className={`flex min-w-0 items-center gap-1 truncate font-bold ${isMulti ? 'text-sm' : 'text-base'}`}>
                  <span className="truncate">{player.name}</span>
                  {idx === gameState.starterPlayerIndex && (
                    <span className="text-amber-600 dark:text-amber-300" title={t('starterLabel')} aria-label={t('starterLabel')}>
                      •
                    </span>
                  )}
                  {isBusted && <span className="rounded-full bg-red-700 px-2 py-0.5 text-xs font-black uppercase text-white">{t('bust')}</span>}
                </h2>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-slate-600 dark:text-slate-400">{t('legsAvg')}</p>
                  <p className="text-xs font-semibold">
                    <span className="font-mono font-bold">{legs}</span> · <span className={`font-mono font-bold ${theme.textAccent}`}>{avg3Dart}</span>
                  </p>
                </div>
              </div>

              <div className={`text-center ${isMulti ? 'py-1' : 'py-1.5 sm:py-2.5'}`}>
                <p
                  className={`font-mono leading-none font-bold tracking-tight ${isMulti ? 'text-3xl sm:text-4xl' : 'text-4xl sm:text-5xl'} ${
                    isBusted ? 'text-red-700 dark:text-red-400' : isActive ? theme.textScore : ''
                  }`}
                >
                  {score}
                </p>

                {isCountUp ? (
                  <div className="mt-1.5 space-y-1">
                    <p className={`text-xs font-semibold ${theme.textAccent}`}>
                      {tf('remainingToTarget', { n: Math.max(0, config.startingScore - score), pct })}
                    </p>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                      <div className={`h-full rounded-full transition-all duration-300 ${theme.keypadPrimaryBg}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ) : playerAdvice ? (
                  <p className={`mt-1 truncate text-xs font-semibold ${isActive ? theme.textAccent : 'text-amber-800 dark:text-amber-300'}`}>
                    {playerAdvice.kind === 'finish' ? t('checkoutLabel') : t('setupLabel')}:{' '}
                    <span className="font-mono font-bold">{playerAdvice.darts.join(' ')}</span>
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{t('noCheckout')}</p>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-black/5 pt-1.5 text-xs text-slate-600 dark:border-white/10 dark:text-slate-400">
                <span title={t('first9Hint')}>
                  {t('first9Label')}: <strong className="font-mono text-slate-900 dark:text-slate-100">{first9Avg}</strong>
                </span>
                <span title={t('checkoutRateHint')}>
                  {t('checkoutRateLabel')}:{' '}
                  <strong className="font-mono text-slate-900 dark:text-slate-100">
                    {hits}/{attempts}
                  </strong>
                </span>
              </div>
            </section>
          );
        })}
      </div>

      {advice && (
        <div
          aria-live="polite"
          className={`flex items-center justify-between gap-2 rounded-2xl p-2.5 sm:p-3 ${
            advice.kind === 'finish' ? 'bg-amber-500/15' : 'bg-black/5 dark:bg-white/5'
          }`}
        >
          <span className="min-w-0 text-sm font-medium text-amber-950 dark:text-amber-100">
            {advice.kind === 'finish' ? routeLabel : tf('setupAdvice', { n: advice.leave })}
            {pending.length > 0 && <span className="block text-xs text-slate-600 dark:text-slate-400">{tf('dartsLeftLabel', { n: 3 - pending.length, score: liveRemaining })}</span>}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            {advice.darts.map((target, i) => (
              <span
                key={i}
                className={`rounded-xl px-2.5 py-1 font-mono text-sm font-black ${
                  advice.kind === 'finish' ? 'bg-amber-500 text-black' : 'bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900'
                }`}
              >
                {target}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
